import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { ApplicationRef, ErrorHandler, LOCALE_ID, NgModule, APP_ID, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Observable } from 'rxjs';

import { AppRoutingModule } from './app-routing.module';
import { MaterialModule } from './common-modules/material.module';
import { CoreModule } from './core/core.module';

import { environment } from '../environments/environment';
import { GprMaterials_BASE_URL, GprMaterialsV1Service } from './core/services/GPRMV1Service.Generated';

import { AppComponent } from './app.component';
import { PageNotFoundComponent } from './core/pages/page-not-found/page-not-found.component';
import { authAppInitializerFactory, AuthService } from './core/services/auth.service';
import { InterceptorService } from './core/services/interceptor.service';
import { CustomToasterModule } from './core/services/toaster/toaster.module';

// import ngx-translate and the http loader
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { registerLocaleData } from '@angular/common';
import localeNL from '@angular/common/locales/nl';
import { MatomoRouteDataInterceptor, NgxMatomoModule, NgxMatomoRouterModule } from 'ngx-matomo-client';
import { AppCompatibilityComponent } from './app-compatibility/app-compatibility.component';
import { GlobalErrorHandlerService } from './global-error-handler.service';
import { PrintPageModule } from './print-page/print-page.module';
import { ReCalculateService } from './core/services/recalculate.service';

registerLocaleData(localeNL);

export function httpLoaderFactory(http: HttpClient): TranslateLoader {
  return {
    getTranslation(lang: string): Observable<object> {
      return http.get(`/assets/i18n/${lang}.json`) as Observable<object>;
    }
  };
}


@NgModule({
  declarations: [
    AppComponent,
    AppCompatibilityComponent,
    PageNotFoundComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    AppRoutingModule,
    CustomToasterModule,
    CoreModule,
    PrintPageModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgxMatomoModule.forRoot(environment.matomoConfig),
    NgxMatomoRouterModule.forRoot({
      interceptors: [MatomoRouteDataInterceptor],
    })
  ],
  providers: [
      GprMaterialsV1Service,
      ReCalculateService,
    provideAppInitializer(() => {
        const initializerFn = (authAppInitializerFactory)(inject(AuthService));
        return initializerFn();
      }),
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    { provide: GprMaterials_BASE_URL, useValue: environment.gprMaterialBaseUrl },
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: APP_ID, useValue: 'ng-cli-universal' }
  ]
})
export class AppModule {
  ngDoBootstrap(app: ApplicationRef) {
    const componentSelector = checkBrowserCompatibility();
    bootstrapRootComponent(app, componentSelector);
  }
}

function checkBrowserCompatibility(): string {
  const userAgent = window.navigator.userAgent;

  // Chrome "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/80.0.3987.149 Safari/537.36"
  const isChrome = (userAgent.indexOf('Chrome/')) > -1;

  // Firefox "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:75.0) Gecko/20100101 Firefox/75.0"
  const isFirefox = (userAgent.indexOf('Firefox/')) > -1;

  // Safari  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_4) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1 Safari/605.1.15"
  const isSafari = (userAgent.indexOf('Safari/')) > -1;

  // Internet Explorer "Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; rv:11.0) like Gecko"
  const isIE = (userAgent.indexOf('Trident/')) > -1;

  // Edge "Mozilla/5.0 (Windows NT 10.0; Win64; x64; ServiceUI 14) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.102 Safari/537.36 Edge/18.18362"
  const isEdge = (userAgent.indexOf('Edge/')) > -1;

  const rootElement = document.getElementById('root-element');

  if (isChrome || isEdge || isFirefox || isSafari) {
    return 'app-root';
  }
  return 'app-compatibility';
}

function bootstrapRootComponent(applicationRef: ApplicationRef, selector: string) {
  const components = {
    'app-compatibility': AppCompatibilityComponent,
    'app-root': AppComponent
  };

  const appSpinner = document.querySelector('.app-loading');
  appSpinner.parentElement.removeChild(appSpinner);

  // create DOM element for the component being bootstrapped
  const componentElement = document.createElement(selector);
  document.body.appendChild(componentElement);

  // bootstrap the application with the selected component
  const component = components[selector];
  applicationRef.bootstrap(component);
}
